import React from 'react';
import { getRoutesList as getDefaultRoutes } from '@om-fe/core/core/routing/default-routes';
import { DefaultRoutesProps, RouteModel, RouteNames, customRoutePath, customRoutePathModel } from '@om-fe/core/core/routing/utils';
import { DefaultTemplate } from '../templates/default-template';
import LiveTemplate from '../templates/live-template';
import Home from '../pages/home';
import VirtualSportPage from '../pages/virtual-sports-page';
import { VirtualSport } from '../pages/virtual-sports';
import LiveSports from '../pages/live-sports';
import SportLocation from '../pages/sport-location';
import SportCategory from '../pages/sport-category';
import SportParent from '../pages/sport-parent';
import EventPage from '../pages/event-page';
import LiveEventPage from '../pages/live-event-page';
import { BettingHistoryPage } from '../pages/betting-history-page';
import { BettingHistoryDetailsPage } from '../pages/betting-history-details-page';
import { TournamentPage } from '../pages/tournament.page';
import { SharedBetPage } from '../pages/shared-bet-page';
import { SpecialBonusPage } from '../pages/special-bonus-page';
import PageNotFound from '@om-fe/core/pages/theme1/404';
import getConfig, { OMFE2ConfigModel } from '@om-fe/core/utils/config';
import { FutureRaceListPage } from '../pages/next-race/future-race-list-page';
import { NextRaceEventPage } from '../pages/next-race/next-race-event-page';
import { BetslipWrapperDesktop } from '../components/betslip-wrapper/betslip-wrapper-desktop';
import { internal_gettext as gettext } from '@om-fe/core/core/i18n/utils';
import { EsportsPage } from '../pages/esport-page';
import SportLocationEsport from '../pages/sport-location-esport';
import { TournamentEsportPage } from '../pages/tournament-esport-page';
import { MatchOfTheDayPage } from '../pages/match-of-the-day-page'

export enum CustomRouteNames {
    home = 'homeTab',
}

const config: OMFE2ConfigModel = getConfig();
const blocked: RouteNames[] = config?.leftSideBar?.coreHelpSupportLinks ? [] : [RouteNames.betBuilderRules, RouteNames.cashOutTerms];
const specialBonusSingle: boolean = config?.specialBonus?.single;
const specialBonusMulti: boolean = config?.specialBonus?.multi;
const deepLink: boolean = !!(config?.betslip?.deepLink);
const enabledHorseRacing: boolean = config?.enabledHorseRacing ?? false;
const enabledGreyHound: boolean = config?.enabledGreyHound ?? false;
const enabledEsportPage: boolean = config?.enabledEsportPage ?? false;
const hasMatchOfTheDayPage: boolean = config?.hasMatchOfTheDayPage ?? false;
const enabledShareBetSelection: boolean = config?.betslip?.enabledShareBetSelection ?? false;
const deepLinkPath: string = deepLink ? `/:oddName?/:bettingOfferIds?/:stakeName?/:stake?` : ``;
const enableHyphenatedUrl: boolean = !!(getConfig().enableHyphenatedUrl && Object.keys(getConfig().enableHyphenatedUrl || {}).length > 0);

function getOperatorCustomRoutes({ lang }: DefaultRoutesProps): Array<Partial<RouteModel>> {
    const path: customRoutePathModel = customRoutePath(deepLinkPath, lang);
    let newRoutes: Array<Partial<RouteModel>> = [
        {
            name: RouteNames.home,
            component: Home,
        },
        {
            name: RouteNames.topBets,
            path: `/top-bets/:sportRoute/:locationRoute?/:timeRoute?`,
            exact: true,
            component: Home,
            template: DefaultTemplate,
        },
        {
            name: RouteNames.bonusOfTheDay,
            path: `/bonus-of-the-day`,
            exact: true,
            component: SpecialBonusPage,
            template: DefaultTemplate,
        },
        {
            name: CustomRouteNames.home,
            component: Home,
            path: `/tab/:homeTab?`,
            exact: true,
            template: DefaultTemplate,
        },
        {
            name: RouteNames.liveSports,
            component: LiveSports,
        },
        {
            name: RouteNames.sportCategory,
            component: SportCategory,
        },
        {
            name: RouteNames.sportLocation,
            component: SportLocation,
        },
        {
            name: RouteNames.sportParent,
            component: SportParent,
        },
        {
            name: RouteNames.event,
            component: EventPage,
        },
        {
            name: RouteNames.liveEvent,
            component: LiveEventPage,
        },
        {
            name: RouteNames.liveOutright,
            component: LiveEventPage,
        },
        {
            name: RouteNames.bettingHistory,
            component: BettingHistoryPage,
        },
        {
            name: RouteNames.betDetails,
            component: BettingHistoryDetailsPage,
        },
        {
            name: RouteNames.tournamentDiscipline,
            component: TournamentPage,
        },
        {
            name: RouteNames.tournamentCategories,
            component: TournamentPage,
        },
        {
            name: RouteNames.tournamentLocation,
            component: TournamentPage,
        },
        {
            name: RouteNames.sharedBet,
            component: SharedBetPage,
        },
        {
            name: RouteNames.deepLink,
            component: deepLink ? Home : PageNotFound,
        },
        {
            name: RouteNames.betSlip,
            component: BetslipWrapperDesktop,
        },
        {
            name: RouteNames.virtualSports,
            component: VirtualSportPage,
            path: '/virtual',
            exact: true,
            template: DefaultTemplate,
        },
        {
            name: RouteNames.newVirtualSports,
            path: `${gettext('/virtual-sports', null, lang)}/:virtualSportsName?/:virtualSportsId?/:virtualTournamentName?/:virtualTournamentId?`,
            component: VirtualSport,
            exact: true,
            template: DefaultTemplate,
            meta: {
                hasNoSearchTrigger: true,
            },
        },
        {
            name: RouteNames.MatchOfTheDay,
            path: `${gettext('/match-of-the-day', null, lang)}`,
            component: hasMatchOfTheDayPage ? MatchOfTheDayPage : PageNotFound,
            exact: true,
            template: DefaultTemplate,
            meta: {
                hasNoSearchTrigger: true,
            },
        }
    ];

    if (config?.customConfig?.externalBettingRules) {
        newRoutes = [
            ...newRoutes,
            {
                name: RouteNames.bettingRules,
                component: PageNotFound,
            }
        ];
    }

    if (!specialBonusSingle && !specialBonusMulti) {
        newRoutes = [
            ...newRoutes,
            {
                name: RouteNames.bonusOfTheDay,
                component: PageNotFound,
            }
        ];
    }

    if (enabledHorseRacing || enabledGreyHound) {
        newRoutes = [
            ...newRoutes,
            {
                name: RouteNames.futureRaceList,
                path: enableHyphenatedUrl ? path.customFutureRaceList : `/future-race-list/:sportId/:eventId`,
                exact: true,
                component: FutureRaceListPage,
                template: DefaultTemplate,
            },
            {
                name: RouteNames.nextRaceEvent,
                path: enableHyphenatedUrl ? path.customNextRaceEvent : `/next-race-event/:sportId/:eventId`,
                exact: true,
                component: NextRaceEventPage,
                template: DefaultTemplate,
            },
        ];
    }

    if (enabledEsportPage) {
        newRoutes = [
            ...newRoutes,
            {
                name: RouteNames.eSports,
                path: `${gettext('/e-sports', null, lang)}`,
                component: EsportsPage,
                exact: true,
                isEsportsRoute: true,
            },
            {
                name: RouteNames.eSportsParent,
                path: `${gettext('/e-sports', null, lang)}/:disciplineName/:disciplineId(\\d+)${gettext('/discipline', null, lang)}/:activeTab?`,
                component: SportLocationEsport,
                exact: true,
                template: DefaultTemplate,
                isEsportsRoute: true,
            },
            {
                name: RouteNames.eSportsTournament,
                path: `${gettext('/e-sports', null, lang)}${gettext('/tournament', null, lang)}/:disciplineName/:disciplineId(\\d+)/:tournamentName/:tournamentId(\\d+)/:activeTab?`,
                component: TournamentEsportPage,
                exact: true,
                template: DefaultTemplate,
                isEsportsRoute: true,
            },
            {
                name: RouteNames.eSportsTournamentLocation,
                path: `${gettext('/e-sports', null, lang)}${gettext('/tournament-location', null, lang)}/:disciplineName/:disciplineId(\\d+)/:locationName/:locationId(\\d+)/:tournamentName/:tournamentId(\\d+)/:activeTab?`,
                component: TournamentEsportPage,
                exact: true,
                template: DefaultTemplate,
                isEsportsRoute: true,
            },
            {
                name: RouteNames.eSportsEvent,
                path: `${gettext('/e-sports', null, lang)}${gettext('/event', null, lang)}/:disciplineId/:disciplineName/:location/:tournament/:name/:id(\\d+)/:activeMarketGroupTab?`,
                component: EventPage,
                exact: true,
                template: DefaultTemplate,
                isEsportsRoute: true,
            },
            {
                name: RouteNames.eSportsLiveEvent,
                path: `${gettext('/e-sports', null, lang)}${gettext('/live-event', null, lang)}/:disciplineId?/:disciplineName?/:location?/:tournament?/:name?/:id(\\d+)?/:activeMarketGroupTab?`,
                component: LiveEventPage,
                exact: true,
                template: LiveTemplate,
                isEsportsRoute: true,
            },
        ];
    }

    if (enabledShareBetSelection) {
        newRoutes = [
            ...newRoutes,
            {
                name: RouteNames.bookingCode,
                path: `${gettext('/bookingcode', null, lang)}/:bookingCode`,
                exact: true,
                component: Home,
                template: DefaultTemplate,
            },
        ];
    }

    return getDefaultRoutes({
        lang,
        newRoutes,
        templates: { defaultTemplate: DefaultTemplate, liveTemplate: LiveTemplate },
    }).filter((route) => { return !blocked.includes(route.name); });
}

export default getOperatorCustomRoutes;
